"use client";

import { isSocialTrafficSource } from "@/components/AdobeDataLayer/utils";
import { PHONE_NUMBERS } from "@/constants/phone";
import usePhoneNumber from "@/hooks/use-phone-number";
import fireElementClicked from "@/lib/tagular/fire-element-clicked";
import { TAGULAR_ELEMENT } from "@/types/tagular";
import { usePathname } from "next/navigation";

export interface PhoneNumberProps extends React.HTMLAttributes<HTMLAnchorElement> {
  fallbackPhone?: string;
  tagularProps?: TAGULAR_ELEMENT;
  /**
   * Content rendered before the number inside the link (e.g. an icon and
   * label). When set, Fuse targets only the number so it can't overwrite it.
   */
  children?: React.ReactNode;
}

const PhoneNumber = ({
  fallbackPhone = PHONE_NUMBERS.DEFAULT,
  tagularProps,
  children,
  onClick,
  ...props
}: PhoneNumberProps) => {
  const pathname = usePathname();
  const isSocial = isSocialTrafficSource(pathname);
  const phoneNumber = usePhoneNumber(fallbackPhone);
  const fuseProps = isSocial
    ? {}
    : {
        "data-fuse": true,
        "data-fuse-format": "1-###-###-####",
      };

  // suppressHydrationWarning: Fuse script modifies data-fuse elements before React hydrates (BAU only)
  return (
    <a
      {...props}
      href={`tel:${phoneNumber}`}
      {...(children ? {} : fuseProps)}
      suppressHydrationWarning
      onClick={(event) => {
        fireElementClicked({
          actionOutcome: "phoneNumber",
          elementType: "phoneNumber",
          text: phoneNumber,
          ...tagularProps,
        });
        onClick?.(event);
      }}
    >
      {children ? (
        <>
          {children}
          <span {...fuseProps} suppressHydrationWarning>
            {phoneNumber}
          </span>
        </>
      ) : (
        phoneNumber
      )}
    </a>
  );
};

export default PhoneNumber;
